import { getToken, setToken, removeToken } from '../utils/tokenStorage';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import NotificationBell from '../components/NotificationBell';
import AdminAnnouncementsTab from '../components/dashboard/AdminAnnouncementsTab';
import AdminMembersTab from '../components/dashboard/AdminMembersTab';
import AdminInquiriesTab from '../components/dashboard/AdminInquiriesTab';
import AdminGroupsTab from '../components/dashboard/AdminGroupsTab';
import AdminPropertiesTab from '../components/dashboard/AdminPropertiesTab';
import AdminApplicationsTab from '../components/dashboard/AdminApplicationsTab';
import AdminPaymentsTab from '../components/dashboard/AdminPaymentsTab';
import AdminSupportTab from '../components/dashboard/AdminSupportTab';
import './AdminDashboard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Derive initial tab from URL path (e.g. /admin/applications -> 'applications')
  const pathTab = location.pathname.replace(/^\/admin\/?/, '').split('/')[0];
  const queryTab = new URLSearchParams(location.search).get('tab') || '';
  const validTabs = ['applications', 'properties', 'members', 'inquiries', 'group-apps', 'manage-groups', 'announcements', 'payments', 'support'];
  const [activeTab, setActiveTab] = useState(
    validTabs.includes(pathTab) ? pathTab : validTabs.includes(queryTab) ? queryTab : 'applications'
  );
  const [applications, setApplications] = useState([]);
  const [properties, setProperties] = useState([]);
  const [members, setMembers] = useState([]);
  const [interests, setInterests] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [adminNotes, setAdminNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [stats, setStats] = useState({ applications: 0, pending: 0, properties: 0, members: 0, interests: 0 });
  const [selectedApps, setSelectedApps] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [adminGroups, setAdminGroups] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [announcementForm, setAnnouncementForm] = useState({ subject: '', body: '', send_email: true });
  const [memberDetail, setMemberDetail] = useState(null);
  const [individualMsg, setIndividualMsg] = useState({ user_id: '', subject: '', message: '' });
  const [groupSupportRequests, setGroupSupportRequests] = useState([]);
  const [photoCsvData, setPhotoCsvData] = useState([]);
  const [photoUpdateResult, setPhotoUpdateResult] = useState(null);
  const [photoUpdating, setPhotoUpdating] = useState(false);

  const fetchAllData = useCallback(async () => {
    try {
      const token = getToken();
      const config = { withCredentials: true, headers: { Authorization: `Bearer ${token}` } };
      
      const [appsRes, propsRes, membersRes, interestsRes, groupSuppRes, groupsRes, annsRes] = await Promise.all([
        axios.get(`${API}/applications`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/properties`).catch(() => ({ data: [] })),
        axios.get(`${API}/members`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/interests`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/group-support-requests`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/groups`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/announcements`, config).catch(() => ({ data: [] }))
      ]);
      
      setApplications(appsRes.data);
      setProperties(propsRes.data);
      setMembers(membersRes.data);
      setInterests(interestsRes.data);
      setGroupSupportRequests(groupSuppRes.data);
      setAdminGroups(groupsRes.data);
      setAnnouncements(annsRes.data);
      
      setStats({
        applications: appsRes.data.length,
        pending: appsRes.data.filter(a => a.status === 'pending').length,
        properties: propsRes.data.length,
        members: membersRes.data.length,
        interests: interestsRes.data.filter(i => i.status === 'new').length
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const url = filterStatus === 'all' 
        ? `${API}/applications` 
        : `${API}/applications?status=${filterStatus}`;
      const response = await axios.get(url, { 
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    if (activeTab === 'applications') fetchApplications();
  }, [filterStatus, fetchApplications]);

  const viewApplication = async (app) => {
    setSelectedApp(app);
    try {
      const token = getToken();
      const response = await axios.get(`${API}/admin-notes/${app.application_id}`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminNotes(response.data);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    }
  };

  const updateApplicationStatus = async (applicationId, status) => {
    try {
      const token = getToken();
      await axios.patch(
        `${API}/applications/${applicationId}/status?status=${status}`,
        {},
        { withCredentials: true, headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Application ${status} successfully! Email sent to applicant.`);
      setSelectedApp(null);
      fetchApplications();
      fetchAllData();
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update application status');
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      const token = getToken();
      await axios.post(
        `${API}/admin-notes?application_id=${selectedApp.application_id}&note=${encodeURIComponent(newNote)}`,
        {},
        { withCredentials: true, headers: { Authorization: `Bearer ${token}` } }
      );
      setNewNote('');
      const response = await axios.get(`${API}/admin-notes/${selectedApp.application_id}`, {
        withCredentials: true, headers: { Authorization: `Bearer ${token}` }
      });
      setAdminNotes(response.data);
    } catch (error) {
      console.error('Failed to add note:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      removeToken();
      navigate('/');
    } catch (error) {
      removeToken();
      navigate('/');
    }
  };

  const deleteProperty = async (propertyId) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    try {
      const token = getToken();
      await axios.delete(`${API}/properties/${propertyId}`, {
        withCredentials: true, headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(properties.filter(p => p.property_id !== propertyId));
      setStats(prev => ({ ...prev, properties: prev.properties - 1 }));
    } catch (error) {
      alert('Failed to delete property');
    }
  };

  const handlePhotoCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const rows = text.split('\n').map(r => r.trim()).filter(r => r);
      const headers = rows[0].split(',').map(h => h.toLowerCase().trim());
      const pidIdx = headers.findIndex(h => h.includes('property_id') || h === 'property id');
      const imgIdx = headers.findIndex(h => h.includes('image_url') || h === 'image url' || h === 'image');
      if (pidIdx === -1 || imgIdx === -1) {
        alert('CSV must have columns: property_id, image_url');
        return;
      }
      const data = rows.slice(1).map(row => {
        const cols = row.split(',');
        return { property_id: (cols[pidIdx] || '').trim(), image_url: (cols[imgIdx] || '').trim() };
      }).filter(r => r.property_id && r.image_url);
      setPhotoCsvData(data);
      setPhotoUpdateResult(null);
    };
    reader.readAsText(file);
  };

  const submitBulkPhotoUpdate = async () => {
    if (!photoCsvData.length) return;
    setPhotoUpdating(true);
    try {
      const token = getToken();
      const res = await axios.post(`${API}/admin/properties/bulk-photo`,
        { updates: photoCsvData },
        { withCredentials: true, headers: { Authorization: `Bearer ${token}` } }
      );
      setPhotoUpdateResult(res.data);
      setPhotoCsvData([]);
      // Refresh properties
      const propsRes = await axios.get(`${API}/properties`).catch(() => ({ data: [] }));
      setProperties(propsRes.data);
    } catch (err) {
      alert('Bulk photo update failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPhotoUpdating(false);
    }
  };

  const toggleSelectApp = (appId) => {
    setSelectedApps(prev => prev.includes(appId) ? prev.filter(id => id !== appId) : [...prev, appId]);
  };

  const toggleSelectAll = () => {
    const filtered = filteredApplications();
    if (selectedApps.length === filtered.length) {
      setSelectedApps([]);
    } else {
      setSelectedApps(filtered.map(a => a.application_id));
    }
  };

  const bulkAction = async (status) => {
    if (selectedApps.length === 0) return alert('No applications selected');
    if (!window.confirm(`${status === 'approved' ? 'Approve' : 'Reject'} ${selectedApps.length} application(s)?`)) return;
    setBulkLoading(true);
    try {
      const token = getToken();
      const res = await axios.post(`${API}/applications/bulk-action`, {
        application_ids: selectedApps, status
      }, { withCredentials: true, headers: { Authorization: `Bearer ${token}` } });
      alert(res.data.message);
      setSelectedApps([]);
      fetchAllData();
    } catch (error) {
      alert('Bulk action failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const filteredApplications = () => {
    return filterStatus === 'all' ? applications : applications.filter(a => a.status === filterStatus);
  };

  const downloadCSV = async (endpoint, filename) => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}${endpoint}`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed');
    }
  };

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard"><header className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <img src="/logo.png" alt="House Sharing Seniors" className="dashboard-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
              <h1>Admin Dashboard</h1>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button className="btn btn-primary" onClick={() => navigate('/properties/add')}>+ Add Property</button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <NotificationBell />
                <button className="btn btn-secondary" onClick={handleLogout} data-testid="logout-btn">Logout</button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container">
        {/* Stats Cards */}
        <div className="stats-row">
          <div className="stat-card" onClick={() => setActiveTab('applications')}>
            <div className="stat-number">{stats.applications}</div>
            <div className="stat-label">Total Applications</div>
          </div>
          <div className="stat-card stat-pending" onClick={() => { setActiveTab('applications'); setFilterStatus('pending'); }}>
            <div className="stat-number">{stats.pending}</div>
            <div className="stat-label">Pending Review</div>
          </div>
          <div className="stat-card" onClick={() => setActiveTab('properties')}>
            <div className="stat-number">{stats.properties}</div>
            <div className="stat-label">Properties</div>
          </div>
          <div className="stat-card" onClick={() => setActiveTab('members')}>
            <div className="stat-number">{stats.members}</div>
            <div className="stat-label">Approved Members</div>
          </div>
          <div className="stat-card stat-pending" onClick={() => setActiveTab('inquiries')}>
            <div className="stat-number">{stats.interests}</div>
            <div className="stat-label">New Inquiries</div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="admin-tabs">
          <button className={`admin-tab ${activeTab === 'applications' ? 'active' : ''}`} onClick={() => setActiveTab('applications')}>
            Applications
          </button>
          <button className={`admin-tab ${activeTab === 'properties' ? 'active' : ''}`} onClick={() => setActiveTab('properties')}>
            Properties
          </button>
          <button className={`admin-tab ${activeTab === 'members' ? 'active' : ''}`} onClick={() => setActiveTab('members')}>
            Members
          </button>
          <button className={`admin-tab ${activeTab === 'inquiries' ? 'active' : ''}`} onClick={() => setActiveTab('inquiries')}>
            Inquiries {stats.interests > 0 && <span style={{ background: '#ef4444', color: 'white', borderRadius: '10px', padding: '2px 8px', fontSize: '12px', marginLeft: '6px' }}>{stats.interests}</span>}
          </button>
          <button className={`admin-tab ${activeTab === 'group-apps' ? 'active' : ''}`} onClick={() => setActiveTab('group-apps')} data-testid="group-apps-tab">
            Group Support {groupSupportRequests.filter(a => a.status === 'open').length > 0 && <span style={{ background: '#ef4444', color: 'white', borderRadius: '10px', padding: '2px 8px', fontSize: '12px', marginLeft: '6px' }}>{groupSupportRequests.filter(a => a.status === 'open').length}</span>}
          </button>
          <button className={`admin-tab ${activeTab === 'manage-groups' ? 'active' : ''}`} onClick={() => setActiveTab('manage-groups')} data-testid="manage-groups-tab">
            Property Groups ({adminGroups.length})
          </button>
          <button className={`admin-tab ${activeTab === 'announcements' ? 'active' : ''}`} onClick={() => setActiveTab('announcements')} data-testid="announcements-tab">
            Announcements
          </button>
          <button className={`admin-tab ${activeTab === 'payments' ? 'active' : ''}`} onClick={() => setActiveTab('payments')} data-testid="payments-tab">
            Payments
          </button>
          <button className={`admin-tab ${activeTab === 'support' ? 'active' : ''}`} onClick={() => setActiveTab('support')} data-testid="support-tab">
            Support
          </button>
        </div>

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <AdminApplicationsTab
            applications={applications}
            filteredApplications={filteredApplications}
            selectedApp={selectedApp}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            selectedApps={selectedApps}
            setSelectedApps={setSelectedApps}
            toggleSelectApp={toggleSelectApp}
            toggleSelectAll={toggleSelectAll}
            bulkLoading={bulkLoading}
            bulkAction={bulkAction}
            viewApplication={viewApplication}
            updateApplicationStatus={updateApplicationStatus}
            loading={loading}
            adminNotes={adminNotes}
            newNote={newNote}
            setNewNote={setNewNote}
            addNote={addNote}
            downloadCSV={downloadCSV}
          />
        )}

        {/* Properties Tab */}
        {activeTab === 'properties' && (
          <AdminPropertiesTab
            properties={properties}
            photoCsvData={photoCsvData}
            photoUpdateResult={photoUpdateResult}
            photoUpdating={photoUpdating}
            handlePhotoCsvUpload={handlePhotoCsvUpload}
            submitBulkPhotoUpdate={submitBulkPhotoUpdate}
            downloadCSV={downloadCSV}
            deleteProperty={deleteProperty}
          />
        )}

        {/* Members Tab - Enhanced Management */}
        {activeTab === 'members' && (
          <AdminMembersTab
            members={members}
            memberDetail={memberDetail}
            setMemberDetail={setMemberDetail}
            onRefresh={fetchAllData}
            setActiveTab={setActiveTab}
            setIndividualMsg={setIndividualMsg}
            downloadCSV={downloadCSV}
          />
        )}

        {activeTab === 'inquiries' && (
          <AdminInquiriesTab interests={interests} onRefresh={fetchAllData} />
        )}

        {(activeTab === 'group-apps' || activeTab === 'manage-groups') && (
          <AdminGroupsTab
            mode={activeTab === 'group-apps' ? 'support' : 'groups'}
            groupSupportRequests={groupSupportRequests}
            adminGroups={adminGroups}
            onRefresh={fetchAllData}
          />
        )}

        {/* Announcements Tab */}
        {activeTab === 'announcements' && (
          <AdminAnnouncementsTab
            members={members}
            announcements={announcements}
            announcementForm={announcementForm}
            setAnnouncementForm={setAnnouncementForm}
            individualMsg={individualMsg}
            setIndividualMsg={setIndividualMsg}
            onRefresh={fetchAllData}
          />
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <AdminPaymentsTab downloadCSV={downloadCSV} />
        )}

        {/* Support Tab */}
        {activeTab === 'support' && (
          <AdminSupportTab />
        )}

      </div>
    </div>
  );
};

export default AdminDashboard;
